import { useCallback, useEffect, useRef, useState } from "react";
import { listarEstudiantes, obtenerEstudiante } from "../../../../services/estudiantesService";
import { normalizeEstudiante, nombreCompleto } from "../adapters/estudiantesAdapter";
import { showToast } from "../../../../utils/showToast";

const initialFilters = { estado_academico: "all", activo: "all", search: "" };

export default function useEstudiantesList() {
  const [estudiantes, setEstudiantes] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({
    total: 0,
    activos: 0,
    inscritos: 0,
    matriculados: 0,
    egresados: 0,
  });

  const fetchEstudiantes = useCallback(async (pageOverride, filtrosOverride) => {
    setLoading(true);
    const currentPage = pageOverride ?? page;
    const currentFiltros = filtrosOverride ?? filtros;
    try {
      const params = {
        pagina: currentPage,
        porPagina: pageSize,
        search: currentFiltros.search || undefined,
        estado_academico: currentFiltros.estado_academico !== 'all' ? currentFiltros.estado_academico : undefined,
        activo: currentFiltros.activo !== 'all' ? currentFiltros.activo : undefined,
      };

      const response = await listarEstudiantes(params);
      const parsed = (Array.isArray(response?.data) ? response.data : []).map(normalizeEstudiante);
      setEstudiantes(parsed);
      setTotal(Number(response?.total || 0));

      const srv = response?.resumen || {};
      setResumen({
        total: Number(srv.total || 0),
        activos: Number(srv.activos || 0),
        inscritos: Number(srv.inscritos || 0),
        matriculados: Number(srv.matriculados || 0),
        egresados: Number(srv.egresados || 0),
      });

      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo obtener estudiantes.");
      showToast({ type: "danger", message: err.message || "No se pudo obtener estudiantes." });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filtros]);

  useEffect(() => {
    fetchEstudiantes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const prevFiltrosRef = useRef(filtros);
  useEffect(() => {
    if (prevFiltrosRef.current === filtros) return;
    prevFiltrosRef.current = filtros;
    setPage(1);
    fetchEstudiantes(1, filtros);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  useEffect(() => {
    const handleUpdated = () => fetchEstudiantes();
    const handleStorage = (event) => {
      if (event.key === "estudiantes:lastUpdate") fetchEstudiantes();
    };
    window.addEventListener("estudiantes:updated", handleUpdated);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("estudiantes:updated", handleUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEstudiantes]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  // nombreCompleto exportado para uso externo (se mantiene para compatibilidad)
  // eslint-disable-next-line no-unused-vars
  const _nombreCompleto = nombreCompleto;

  const upsertEstudiante = useCallback((nuevo) => {
    if (!nuevo?.id) return;
    // Actualizar en página actual si existe; si no, volver a página 1
    setEstudiantes((prev) => {
      const exists = prev.some((item) => String(item.id) === String(nuevo.id));
      if (exists) {
        return prev.map((item) =>
          String(item.id) === String(nuevo.id) ? normalizeEstudiante({ ...item, ...nuevo }) : item
        );
      }
      // Nuevo item: reload en página 1
      fetchEstudiantes(1, filtros);
      return prev;
    });
    setLastUpdated(new Date());
  }, [fetchEstudiantes, filtros]);

  const removeEstudiante = useCallback((id) => {
    setEstudiantes((prev) => prev.filter((item) => item.id !== id));
    setTotal((prev) => Math.max(0, prev - 1));
    setLastUpdated(new Date());
  }, []);

  const seleccionar = useCallback(
    async (estudiante) => {
      if (!estudiante) {
        setSeleccionado(null);
        return;
      }
      const id = estudiante.id || estudiante;
      try {
        const detalle = await obtenerEstudiante(id);
        setSeleccionado(normalizeEstudiante(detalle, id));
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: "No se pudo cargar el estudiante." });
      }
    },
    []
  );

  const limpiarFiltros = useCallback(() => setFiltros(initialFilters), []);

  return {
    estudiantes,
    estudiantesFiltrados: estudiantes,
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    estudiantesPaginados: estudiantes,
    reload: fetchEstudiantes,
    seleccionar,
    upsertEstudiante,
    removeEstudiante,
  };
}
